import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Document} from '../../../../../../models/document';
import {AdditionalDocument} from '../../../../../../models/additional-document';
import {AdditionalDocumentType} from '../../../../../../models/additional-document-type';
import {ToastrService} from 'ngx-toastr';

@Component({
  standalone: false,
  selector: 'app-address-document',
  templateUrl: './address-document.component.html',
  styleUrls: ['./address-document.component.scss']
})
export class AddressDocumentComponent implements OnInit {
  @Input() upload: (document: AdditionalDocument) => Promise<AdditionalDocument>;
  @Input() update: (document: AdditionalDocument) => Promise<AdditionalDocument>;
  @Input() remove: (document: AdditionalDocument) => Promise<boolean>;
  additionalDocumentType = AdditionalDocumentType;
  isBusy = new Array<boolean>();
  @Output() selectedDocumentChange = new EventEmitter<Document>();
  @Output() frontDocumentChange: EventEmitter<AdditionalDocument> = new EventEmitter<AdditionalDocument>();
  @Output() backDocumentChange: EventEmitter<AdditionalDocument> = new EventEmitter<AdditionalDocument>();
  @Input() additionalDocument: AdditionalDocument;
  @Output() additionalDocumentChange: EventEmitter<AdditionalDocument> = new EventEmitter<AdditionalDocument>();
  @Input() submitted: boolean;
  private _acceptExt = [
    '.pdf', '.jpg', '.jpeg', '.png'
  ];

  constructor(
    public toastr: ToastrService
  ) {
  }

  private _backDocument: AdditionalDocument;

  get backDocument(): AdditionalDocument {
    return this._backDocument;
  }

  @Input() set backDocument(value: AdditionalDocument) {
    this._backDocument = value;
  }

  private _frontDocument: AdditionalDocument;

  get frontDocument(): AdditionalDocument {
    return this._frontDocument;
  }

  @Input() set frontDocument(value: AdditionalDocument) {
    this._frontDocument = value;
    this.updateSelectedDocument();
  }

  private _selectedDocument: Document;

  get selectedDocument(): Document {
    return this._selectedDocument;
  }

  @Input() set selectedDocument(value: Document) {
    this._selectedDocument = value;
    this.selectedDocumentChange.emit(value);
  }

  private _documents!: Document[];

  get documents(): Document[] {
    return this._documents;
  }

  @Input() set documents(value: Document[]) {
    this._documents = value;
    this.updateSelectedDocument();
  }

  get acceptFormats(): string {
    return this._acceptExt.join(',');
  }

  ngOnInit() {
  }

  async onLoadFront(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      this.frontDocument = await this.load(file, AdditionalDocumentType.ProofAddressFront);
      this.frontDocumentChange.emit(this.frontDocument);
    }
  }

  async onLoadBack(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      this.backDocument = await this.load(file, AdditionalDocumentType.ProofAddressBack);
      this.backDocumentChange.emit(this.backDocument);
    }
  }

  async onLoadAdditional(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      this.additionalDocument = await this.load(file, AdditionalDocumentType.ProofAddressAdditional);
      this.additionalDocumentChange.emit(this.additionalDocument);
    }
  }

  async onRemoveFront() {
    this.isBusy[AdditionalDocumentType.ProofAddressFront] = true;
    const isDeleted = await this.remove(this.frontDocument);
    if (isDeleted) {
      this.frontDocument = null;
      this.frontDocumentChange.emit(this.frontDocument);
    }

    this.isBusy[AdditionalDocumentType.ProofAddressFront] = false;
  }

  async onRemoveBack() {
    this.isBusy[AdditionalDocumentType.ProofAddressBack] = true;
    const isDeleted = await this.remove(this.backDocument);
    if (isDeleted) {
      this.backDocument = null;
      this.backDocumentChange.emit(this.backDocument);
    }

    this.isBusy[AdditionalDocumentType.ProofAddressBack] = false;
  }

  async onRemoveAdditional() {
    this.isBusy[AdditionalDocumentType.ProofAddressAdditional] = true;
    const isDeleted = await this.remove(this.additionalDocument);
    if (isDeleted) {
      this.additionalDocument = null;
      this.additionalDocumentChange.emit(this.additionalDocument);
    }

    this.isBusy[AdditionalDocumentType.ProofAddressAdditional] = false;
  }

  public onSelectedDocumentChange() {
    this.updateDocuments();
  }

  private load(file: any, documentType: AdditionalDocumentType): Promise<AdditionalDocument> {
    return new Promise<AdditionalDocument>((resolve) => {
      this.isBusy[documentType] = true;

      const reader = new FileReader();

      reader.onload = async (e) => {
        const additionalFile = {
          fileName: file.name,
          fileType: file.type,
          fileLength: file.size,
          type: this._selectedDocument.value,
          docType: documentType,
          value: (reader.result as string).split(',')[1],
        } as AdditionalDocument;

        const document = await this.upload(additionalFile);
        this.isBusy[documentType] = false;
        resolve(document);
      };

      reader.onerror = (e) => {
        console.log(e);
        this.isBusy[documentType] = false;
      };

      reader.readAsDataURL(file);
    });
  }

  private updateDocuments(): void {
    if (this.frontDocument) {
      this.isBusy[AdditionalDocumentType.ProofAddressFront] = true;
      this.frontDocument.type = this.selectedDocument.value;
      this.update(this.frontDocument).then().finally(() => this.isBusy[AdditionalDocumentType.ProofAddressFront] = false);
    }

    if (this.backDocument) {
      this.isBusy[AdditionalDocumentType.ProofAddressBack] = true;
      this.backDocument.type = this.selectedDocument.value;
      this.update(this.backDocument).then().finally(() => this.isBusy[AdditionalDocumentType.ProofAddressBack] = false);
    }

    if (this.additionalDocument) {
      this.isBusy[AdditionalDocumentType.ProofAddressAdditional] = true;
      this.additionalDocument.type = this.selectedDocument.value;
      this.update(this.additionalDocument).then().finally(() => this.isBusy[AdditionalDocumentType.ProofAddressAdditional] = false);
    }
  }

  private updateSelectedDocument() {
    if (!this._documents) {
      return;
    }

    if (this._frontDocument &&
      this._frontDocument.type &&
      this._documents.some(x => x.value === this._frontDocument.type)) {
      this.selectedDocument = this._documents.find(x => x.value === this._frontDocument.type);
    } else if (this._documents.indexOf(this.selectedDocument) < 0) {
      this.selectedDocument = this._documents[0];
    }
  }
}
